import {
  fetchAcceptMission,
  fetchCancelMission,
  fetchFailMission,
  fetchSuccessMission,
} from '@module/apis/client-side/mission';
import type { MissionListItem } from '@module/apis/client-side/mission/type';
import useModalStore from '@module/stores/useModalStore';
import formatMillisecondsToTimeUnit from '@module/utils/time/formatMillisecondsToTimeUnit';
import BaseButton from '@pc/components/atoms/button/BaseButton';
import ToastBody from '@pc/components/atoms/toast/ToastBody';
import dayjs from 'dayjs';
import toast, { type Toast } from 'react-hot-toast';
import MissionUserList from './MissionUserList';
import MissionSupportList from './modal/MissionSupportList';
import ModalMissionSupport from './modal/ModalMissionSupport';
import { MissionItemStatus, MissionTimer, waitTimeLimit } from './resources';
import './MissionItem.scss';
import { cn } from '@module/lib/utils';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import formatNumberWithComma from '@module/utils/formatNumberWithComma';

interface Props {
  item: MissionListItem;
  isBroadcast?: boolean;
  sponRestricted?: boolean;
  refetchMissionList?: () => void;
}

export function MissionItem({ item, isBroadcast, sponRestricted, refetchMissionList }: Props) {
  const { pushModal, popModal } = useModalStore();

  // 미션 시작
  const acceptMission = (item: MissionListItem) => {
    pushModal({
      type: 'CONFIRM_CANCEL_MODAL',
      id: 'MissionItem',
      options: {
        description: '미션을 시작하시겠어요?',
        onClick: async () => {
          try {
            popModal();
            await fetchAcceptMission({ missionIdx: item.idx });
          } catch (e) {
            if (e instanceof BaseHttpResponseError) {
              popModal();
              pushModal({
                type: 'CONFIRM_MODAL',
                id: 'MissionItem',
                options: {
                  description: e.serverResponse.message,
                },
              });
            }
          } finally {
            refetchMissionList?.();
          }
        },
      },
    });
  };
  // 미션 성공
  const successMission = (item: MissionListItem) => {
    if (item.stat !== 'running') {
      toast((m: Toast) => <ToastBody id={m.id} content='진햑중인 미션만 성공할 수 있습니다.' />);
      return;
    }
    pushModal({
      type: 'CONFIRM_CANCEL_MODAL',
      id: 'MissionItem',
      options: {
        description: '미션을 성공하셨나요?\n성공한 미션은 다시 실패로 변경할 수 없습니다.',
        onClick: async () => {
          try {
            popModal();
            const res = await fetchSuccessMission({ missionIdx: item.idx });
            toast((m: Toast) => <ToastBody id={m.id} content={res.message} />);
          } catch (e) {
            if (e instanceof BaseHttpResponseError) {
              pushModal({
                type: 'CONFIRM_MODAL',
                id: 'MissionItem',
                options: {
                  description: e.serverResponse.message,
                },
              });
            }
          } finally {
            refetchMissionList?.();
          }
        },
      },
    });
  };

  // 미션 실패
  const failMission = (item: MissionListItem) => {
    if (item.stat !== 'running') {
      toast((m: Toast) => <ToastBody id={m.id} content='진행중인 미션만 실패처리할 수 있습니다.' />);
      return;
    }
    pushModal({
      type: 'CONFIRM_CANCEL_MODAL',
      id: 'MissionItem',
      options: {
        description: '미션을 실패하셨나요?\r\n실패한 미션은 다시 성공으로 변경할 수 없습니다.',
        onClick: async () => {
          try {
            popModal();
            const res = await fetchFailMission({ missionIdx: item.idx });
            toast((m: Toast) => <ToastBody id={m.id} content={res.message} />);
          } catch (e) {
            if (e instanceof BaseHttpResponseError) {
              pushModal({
                type: 'CONFIRM_MODAL',
                id: 'MissionItem',
                options: {
                  description: e.serverResponse.message,
                },
              });
            }
          } finally {
            refetchMissionList?.();
          }
        },
      },
    });
  };

  // 미션 거부
  const cancelMission = (item: MissionListItem) => {
    if (item.stat !== 'wait') {
      toast((m: Toast) => <ToastBody id={m.id} content='대기중인 미션만 취소할 수 있습니다.' />);
      return;
    }
    pushModal({
      type: 'CONFIRM_CANCEL_MODAL',
      id: 'MissionItem',
      options: {
        description: '거부하시면 미션이 취소되어 후원 하트가 다시 반환됩니다.\r\n거부하시겠어요?',
        onClick: async () => {
          try {
            popModal();
            const res = await fetchCancelMission({ missionIdx: item.idx });
            toast((m: Toast) => <ToastBody id={m.id} content={res.message} />);
          } catch (e) {
            if (e instanceof BaseHttpResponseError) {
              pushModal({
                type: 'CONFIRM_MODAL',
                options: {
                  description: e.serverResponse.message,
                },
              });
            }
          } finally {
            refetchMissionList?.();
          }
        },
      },
    });
  };

  const missionSupport = () => {
    if (sponRestricted) return;
    // 추가 후원
    pushModal({ content: <ModalMissionSupport item={item} />, id: 'MissionItem' });
  };

  const missionSupportList = () => {
    if (sponRestricted) return;
    // 후원 리스트
    pushModal({ content: <MissionSupportList item={item} />, id: 'MissionItem' });
  };

  // 대기
  const { day: limitDay, hour: limitHour, minute: limitMinute } = formatMillisecondsToTimeUnit(item.limitTime * 1000);

  const isWaitRunning = item.stat === 'wait' || item.stat === 'running';

  return (
    <div className='mission-wrapper'>
      {isBroadcast && <MissionItemStatus item={item} />}
      <div
        className={cn('mission-item', {
          stemp: item.stat === 'fail' || item.stat === 'success',
        })}
      >
        <div className='top'>
          <div className={cn('status', { [item.stat]: true })}>{item.statHuman}</div>
          <div className='date'>
            {item.stat === 'wait' && <div>{`제한시간 ${limitDay}일 ${limitHour}시간 ${limitMinute}분`}</div>}
            {(item.stat === 'success' || item.stat === 'fail') && (
              <div>{dayjs(item.statChangeDateTime).format('YY년 MM월 DD일 HH:mm:ss')}</div>
            )}
            {item.stat === 'cancel' && <p className='text-[#777]'>미션취소</p>}
            {item.stat === 'running' && (
              <div className='min'>
                <MissionTimer
                  stat={item.stat}
                  limitDateTime={item.limitDateTime}
                  refetchMissionList={refetchMissionList}
                />
              </div>
            )}
          </div>
          {isBroadcast && <div className='wait-timer'>{waitTimeLimit(item)}</div>}
        </div>
        <div className='mission-subject'>{item.title}</div>
        <div className='middle' onClick={missionSupportList}>
          <MissionUserList nameList={item.sponInfo} />
          <div className='nikname'>{item.sponInfo[0].userNick}</div>
        </div>
        <div className={cn('heart', { addMb: isBroadcast })}>
          <div className='mt-[2px] flex items-center gap-[2px]'>
            <p>하트</p>
            <span className='font-[700] font-nanum-square'>{formatNumberWithComma(item.coin)}</span>
          </div>
          {isWaitRunning && !sponRestricted && !isBroadcast && (
            <div className='heartBtn' onClick={missionSupport}>
              +후원
            </div>
          )}
        </div>
        {isBroadcast && isWaitRunning && (
          <div className='bottom-btn'>
            {item.stat === 'wait' && (
              <BaseButton variant='primary' onClick={() => acceptMission(item)}>
                시작
              </BaseButton>
            )}
            {item.stat === 'running' && (
              <BaseButton variant='primary' onClick={() => successMission(item)}>
                성공
              </BaseButton>
            )}

            {item.stat === 'wait' && (
              <BaseButton variant='gray' onClick={() => cancelMission(item)}>
                거부
              </BaseButton>
            )}
            {item.stat === 'running' && (
              <BaseButton variant='gray' onClick={() => failMission(item)}>
                실패
              </BaseButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
